import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import ResumeCard from './ResumeCard';
import { useTranslation } from 'react-i18next';
import { fetchEducations } from '../../api';
import { queryKeys } from '../../api/keys';

const EducationSkeleton = ({ isRTL }) => {
  return (
    <div
      className={`mt-0 w-full flex flex-col gap-10 ${
        isRTL
          ? 'border-r-[6px] border-r-black'
          : 'border-l-[6px] border-l-black'
      }`}
    >
      {[1, 2, 3].map((item) => (
        <div key={item} className="w-full flex animate-pulse">
          <div className="w-5 md:w-10 flex-shrink-0 relative">
            <div className="mt-16 h-[6px] w-full bg-[#1e1e1e]">
              <span
                className={`absolute w-5 h-5 rounded-full -top-2 ${
                  isRTL ? '-right-3' : '-left-3'
                } bg-[#2c2c2c]`}
              />
            </div>
          </div>

          <div className="w-full rounded-2xl border border-white/5 bg-[#111111] p-6 md:p-8 flex flex-col gap-6">
            <div className="flex justify-between items-start gap-4">
              <div className="flex flex-col gap-3 w-full">
                <div className="h-5 bg-[#2a2a2a] rounded-md w-1/2" />
                <div className="h-4 bg-[#242424] rounded-md w-2/3" />
              </div>

              <div className="h-10 w-28 rounded-lg bg-[#1e1e1e]" />
            </div>

            <div className="flex flex-col gap-3">
              <div className="h-4 bg-[#242424] rounded-md w-full" />
              <div className="h-4 bg-[#242424] rounded-md w-[90%]" />
              <div className="h-4 bg-[#242424] rounded-md w-[75%]" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Education = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;

  const { data: educations, isLoading, error } = useQuery({
    queryKey: queryKeys.educations(currentLang),
    queryFn: () => fetchEducations(currentLang),
  });

  if (isLoading) {
    return <EducationSkeleton isRTL={i18n.language === "ar"} />;
  }

  if (error) {
    return <div className="text-red-500 text-center">Error loading education: {error.message}</div>;
  }

  const sortedEducations = [...educations].sort((a, b) => a.order - b.order);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      className="w-full flex flex-col gap-6"
    >
      <div>
        <div className="py-6 lgl:py-12 font-titleFont flex flex-col gap-4">
          <p className="text-sm text-designColor tracking-[4px]">{t("resume.education.feature.date")}</p>
          <h2 className="text-3xl md:text-4xl font-bold">{t("resume.education.feature.title")}</h2>
        </div>
        <div className={`mt-0 w-full border-opacity-30 flex flex-col gap-10 
          ${i18n.language === "ar" ? "border-r-[6px] border-r-black" : "border-l-[6px] border-l-black"}`}>
          {sortedEducations.map((edu) => (
            <ResumeCard
              key={edu.id}
              title={`${edu.degree}${edu.field_of_study ? ` in ${edu.field_of_study}` : ''}`}
              subTitle={edu.institution_name}
              result={edu.year}
              des={edu.description}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default Education;